import { useLayoutEffect } from 'react'

import { getShellContentScrollElement } from 'shared/shell_content_scroll_registry'

let scrollLockCount = 0
let savedHtmlStyles = null
let savedShellContentScrollTop = null

const revertShellContentScrollIfNeeded = (event) => {
  if (scrollLockCount === 0 || savedShellContentScrollTop === null) return

  const shellContentScroll = getShellContentScrollElement()
  if (event.target !== shellContentScroll) return

  if (shellContentScroll.scrollTop !== savedShellContentScrollTop) {
    shellContentScroll.scrollTop = savedShellContentScrollTop
  }
}

let shellScrollListenerInstalled = false

const ensureShellScrollListener = () => {
  if (shellScrollListenerInstalled) return

  shellScrollListenerInstalled = true
  window.addEventListener('scroll', revertShellContentScrollIfNeeded, true)
}

const restoreHtmlStyle = (html, property, value) => {
  if (value) {
    html.style[property] = value
    return
  }

  html.style.removeProperty(property)
}

const lockHtmlScroll = () => {
  ensureShellScrollListener()

  const html = document.documentElement
  const shellContentScroll = getShellContentScrollElement()

  if (savedHtmlStyles === null) {
    savedHtmlStyles = {
      position: html.style.position,
      inset: html.style.inset,
      overflow: html.style.overflow,
    }
    savedShellContentScrollTop = shellContentScroll?.scrollTop ?? null
  }

  html.style.position = 'fixed'
  html.style.inset = '0'
  html.style.overflow = 'hidden'
}

const unlockHtmlScroll = () => {
  if (savedHtmlStyles === null) return

  const html = document.documentElement
  const { position, inset, overflow } = savedHtmlStyles

  restoreHtmlStyle(html, 'position', position)
  restoreHtmlStyle(html, 'inset', inset)
  restoreHtmlStyle(html, 'overflow', overflow)

  savedHtmlStyles = null
  savedShellContentScrollTop = null
}

/**
 * Prevents page scroll jumps while a filter menu is open (e.g. checkbox focus / tabbing).
 * Locks `document.documentElement`. Meetings filters allow `#shell-content-scroll` to move
 * while open; scrollTop is not restored on unlock so the user keeps their current position.
 */
export const useAppShellFilterMenuScrollLock = (isFilterMenuOpen) => {
  useLayoutEffect(() => {
    if (!isFilterMenuOpen) return undefined

    scrollLockCount += 1
    if (scrollLockCount === 1) {
      lockHtmlScroll()
    }

    return () => {
      scrollLockCount -= 1
      if (scrollLockCount === 0) {
        unlockHtmlScroll()
      }
    }
  }, [isFilterMenuOpen])
}
